import React, { useContext, useState } from 'react';
import CountrySelector from '../../Tour/TourBooking/CountrySelector';
import CountryList from 'country-list-with-dial-code-and-flag';
import { Box, Checkbox } from '@mui/material';
import { isBrowser } from '../../../utils/isBrowser';
import LockImage from '../../../images/lock.svg';
import iconArrowRight from '../../../images/short-arrow-right.svg';

import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { AppContext } from '../../../apollo/wrap-root-element';
import { currencySymbols } from '../../../modules/timeline/components/EnterButton/utils';

const DialCodeField = ({ countryCode }) => {
  if (!isBrowser()) return null; // avoid rendering on server side

  const { dialCode } = CountryList.findOneByCountryCode(countryCode);

  return (
    <Box
      className="select"
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        width: '100%',
        backgroundColor: 'white',
      }}
    >
      <span
        className={`fi fi-${countryCode.toLowerCase()}`}
        style={{
          flexShrink: 0,
        }}
      ></span>
      ({dialCode})
    </Box>
  );
};

export default function BillingInformationForm({ formik, isSubmitting, serverError, setActiveStep }) {
  const { currency } = useContext(AppContext);

  const [disabled, setDisabled] = useState(false);
  const { currentUserDetails } = React.useContext(AppContext);

  const { values, handleChange, setFieldValue, errors, touched } = formik;
  const donateAmount = formik.values.donatedProjectsLists.reduce(
    (a, b) => a + (b.customAmount ? +b.customAmount : +b.amount),
    0
  );

  const processingFeeCharge = (donateAmount * 2) / 100;

  return (
    <>
      <div className="form-donate-amount">
        <p className="m-y-32 form-title">Payment Information</p>
        <div className="d-flex m-t-12" style={{ alignItems: 'flex-start' }}>
          <img src={LockImage} />
          <p style={{ marginLeft: '12px' }}>This is a secure SSL encrypted payment.</p>
        </div>

        <div>
          <div className="d-flex columns-2 m-t-32 ">
            <div className="cols pd-x-0 ">
              <label>Card Number*</label>
              <div
                className={`stripe-input-element-container ${
                  touched.cardNumber && errors.cardNumber ? 'form-input-error' : ''
                }`}
              >
                <CardNumberElement
                  options={{
                    style: {
                      base: {
                        fontSize: '14px',
                        width: '100%',
                      },

                      invalid: {
                        color: '#ED2F00',
                        border: '1px solid red',
                      },
                    },
                    showIcon: true,
                  }}
                  onChange={({ empty, complete, error }) => {
                    formik.setFieldValue('cardNumber', complete);

                    if (error && error.message) {
                      formik.setFieldError('cardNumber', error.message);
                    }
                  }}
                />
              </div>
              {touched.cardNumber && <label className="form-error">{errors.cardNumber}</label>}
            </div>
            <div className="cols">
              <label>Expiration date*</label>

              <div
                className={`stripe-input-element-container ${
                  touched.expiryDate && errors.expiryDate ? 'form-input-error' : ''
                }`}
              >
                {/* <img src={CalendarSvg} /> */}

                <CardExpiryElement
                  options={{
                    style: {
                      base: {
                        fontSize: '14px',
                        width: '100%',
                      },
                      invalid: {
                        color: '#ED2F00',
                        border: '1px solid red',
                      },
                    },
                  }}
                  onChange={({ empty, complete, error }) => {
                    formik.setFieldValue('expiryDate', complete);
                    if (error && error.message) {
                      formik.setFieldError('expiryDate', error.message);
                    }
                  }}
                />
              </div>
              {touched.expiryDate && <label className="form-error">{errors.expiryDate}</label>}
            </div>
          </div>

          <div className="d-flex columns-2  ">
            <div className="cols pd-x-0 ">
              <label className="label-margin ">Cardholder Name*</label>
              <input
                type="text"
                name="cardHolderName"
                value={values.cardHolderName}
                onChange={handleChange}
                className={`stripe-input-element-container w-full mg-0 ${
                  touched.cardHolderName && errors.cardHolderName ? 'form-input-error' : ''
                }`}
              />
              {touched.cardHolderName && <label className="form-error">{errors.cardHolderName}</label>}
            </div>
            <div className="cols">
              <label>Security code*</label>
              <div className={`stripe-input-element-container ${touched.cvc && errors.cvc ? 'form-input-error' : ''}`}>
                {/* <img src={LockImage} /> */}
                <div>
                  <CardCvcElement
                    options={{
                      style: {
                        base: {
                          fontSize: '14px',
                          width: 250,
                        },
                        invalid: {
                          color: '#ED2F00',
                          border: '1px solid red',
                        },
                      },
                    }}
                    onChange={({ empty, complete, error }) => {
                      formik.setFieldValue('cvc', complete);
                      if (error && error.message) {
                        formik.setFieldError('cvc', error.message);
                      }
                    }}
                  />
                </div>
              </div>
              {touched.cvc && <label className="form-error">{errors.cvc}</label>}
            </div>
          </div>
        </div>

        <div className="m-t-32">
          <p className="m-t-12 form-title">Billing Information</p>
        </div>
        <div className="d-flex columns-2 m-t-32 gap-17">
          <div className="">
            <label className="label-margin">First Name*</label>
            <input
              type="text"
              name="firstName"
              value={values.firstName}
              onChange={handleChange}
              className={`form-input ${touched.firstName && errors.firstName ? 'form-input-error' : ''}`}
            />
            {touched.firstName && <label className="form-error">{errors.firstName}</label>}
          </div>
          <div className="">
            <label className="label-margin">Last Name*</label>
            <input
              type="text"
              name="lastName"
              value={values.lastName}
              onChange={handleChange}
              className={`form-input ${touched.lastName && errors.lastName ? 'form-input-error' : ''}`}
            />
            {touched.lastName && <label className="form-error">{errors.lastName}</label>}
          </div>
        </div>

        <div className="m-t-16">
          <label className="label-margin">Email address*</label>
          <input
            type="email"
            name="email"
            value={values.email}
            onChange={handleChange}
            className={`w-full form-input ${touched.email && errors.email ? 'form-input-error' : ''}`}
            disabled={!!currentUserDetails?.customer?.email}
          />
          {touched.email && <label className="form-error">{errors.email}</label>}
        </div>

        <div className="m-t-16">
          <label className="label-margin">Choose your Country*</label>
          <CountrySelector countryCode={values.country} setCountryCode={(value) => setFieldValue('country', value)} />
          <label className="form-error">{errors.countryCode}</label>
        </div>
        <div className="m-t-16">
          <label className="label-margin">Phone*</label>
          <div
            style={{
              display: 'flex',
              gap: '10px',
            }}
          >
            <div className="form-input">
              <DialCodeField countryCode={values.country} />
            </div>
            <div
              style={{
                flexGrow: 1,
              }}
            >
              <input
                type="text"
                name="phone"
                value={values.phone}
                onChange={handleChange}
                className={`w-full form-input ${touched.phone && errors.phone ? 'form-input-error' : ''}`}
              />
            </div>
          </div>
          {touched.phone && <label className="form-error">{errors.phone}</label>}
        </div>
        <div className="d-flex columns-2 m-t-16 gap-17">
          <div className="">
            <label className="label-margin">Address*</label>
            <input
              type="text"
              name="addressLine1"
              value={values.addressLine1}
              onChange={handleChange}
              className={`form-input ${touched.addressLine1 && errors.addressLine1 ? 'form-input-error' : ''}`}
            />
            {touched.addressLine1 && <label className="form-error">{errors.addressLine1}</label>}
          </div>
          <div className="">
            <label className="label-margin">Address 2</label>
            <input
              type="text"
              name="addressLine2"
              value={values.addressLine2}
              onChange={handleChange}
              className={`form-input ${touched.addressLine2 && errors.addressLine2 ? 'form-input-error' : ''}`}
            />
            {touched.addressLine2 && <label className="form-error">{errors.addressLine2}</label>}
          </div>
        </div>

        <div className="d-flex columns-2 m-t-16 gap-17">
          <div className="">
            <label className="label-margin">Postal Code*</label>
            <input
              type="text"
              name="zipCode"
              value={values.zipCode}
              onChange={handleChange}
              className={` form-input ${touched.zipCode && errors.zipCode ? 'form-input-error' : ''}`}
            />
            {touched.zipCode && <label className="form-error">{errors.zipCode}</label>}
          </div>
          <div className="">
            <label className="label-margin">City*</label>
            <input
              type="text"
              name="city"
              value={values.city}
              onChange={handleChange}
              className={` form-input ${touched.city && errors.city ? 'form-input-error' : ''}`}
            />
            {touched.city && <label className="form-error">{errors.city}</label>}
          </div>
        </div>
        <div className="m-t-16">
          <label className="label-margin">State/Province*</label>
          <input
            type="text"
            name="state"
            value={values.state}
            onChange={handleChange}
            className={`w-full form-input ${touched.state && errors.state ? 'form-input-error' : ''}`}
          />
          {touched.state && <label className="form-error">{errors.state}</label>}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginTop: '12px',
          }}
        >
          <Checkbox
            disableRipple
            size="small"
            sx={{
              height: '16px',
              mt: '2px',
              padding: 0,
              paddingRight: '9px',
            }}
            value={values.emailTaxReceipts}
            onChange={handleChange}
            name="emailTaxReceipts"
            checked={values.emailTaxReceipts}
          />
          <label
            style={{
              color: '#474747',
            }}
          >
            Email Tax Deductible receipts to me
          </label>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginTop: '12px',
          }}
        >
          <Checkbox
            disableRipple
            size="small"
            sx={{
              height: '16px',
              mt: '2px',
              padding: 0,
              paddingRight: '9px',
            }}
            onChange={handleChange}
            name="enableProcessingFee"
            value={values.enableProcessingFee}
            checked={values.enableProcessingFee}
          />
          <label
            style={{
              color: '#474747',
            }}
          >
            I’d like to add {currencySymbols[currency]} {processingFeeCharge.toFixed(2)} (2%) to cover processing fee
          </label>
        </div>

        <div className="btn-group-submit">
          <button
            type="button"
            className="btn btn-md btn-outline btn-submit previous-button"
            onClick={() => setActiveStep(0)}
          >
            Previous
          </button>

          {isSubmitting ? (
            <button
              type="button"
              className="btn-md btn-outline btn-loading w-full submit-button"
              disabled
              style={{
                cursor: 'not-allowed',
              }}
            >
              <span className="spinner"></span>
            </button>
          ) : (
            <button type="submit" className="btn  btn-primary-ii btn-submit submit-button">
              {`Give ${currencySymbols[currency]}${donateAmount} `}
              <img src={iconArrowRight} width={'15px'} height={'15px'} />
            </button>
          )}
        </div>

        {serverError && <label className="server-error">{serverError}</label>}
      </div>
    </>
  );
}
